import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiLinkedin, FiTwitter, FiGithub } from 'react-icons/fi';
import { CAL_BOOKING_URL } from '../config/calBookingUrl';
import { LINKEDIN_COMPANY_URL } from '../config/socialLinks';
import { FOOTER_HUB_LINKS, getLocaleFromPathname } from '../config/localeRoutes';
import { trackEvent, trackGoal } from './Matomo';
import './Footer.css';

function Footer() {
  const { t } = useTranslation();
  const location = useLocation();
  const locale = getLocaleFromPathname(location.pathname);
  const homePath = locale === 'en' ? '/en/' : '/';
  const hubLinks = FOOTER_HUB_LINKS[locale] || FOOTER_HUB_LINKS.fr;
  const currentYear = new Date().getFullYear();

  const scrollToSection = (sectionId) => {
    const isHome = location.pathname === '/' || location.pathname === '/en/';
    if (!isHome) {
      window.location.href = `${homePath}#${sectionId}`;
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 80;
      const elementPosition = element.offsetTop - headerHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-section">
            <p className="footer-title">Leagora</p>
            <p className="footer-text">
              {t('footer.description')}
            </p>
          </div>

          <div className="footer-section">
            <p className="footer-subtitle">{t('footer.navigation')}</p>
            <ul className="footer-links">
              <li>
                <a href={`${homePath}#fonctionnalites`} onClick={(e) => { e.preventDefault(); scrollToSection('fonctionnalites'); }}>
                  {t('nav.features')}
                </a>
              </li>
              <li>
                <a href={`${homePath}#solutions`} onClick={(e) => { e.preventDefault(); scrollToSection('solutions'); }}>
                  {t('nav.solutions')}
                </a>
              </li>
              <li>
                <a href={`${homePath}#partenaires`} onClick={(e) => { e.preventDefault(); scrollToSection('partenaires'); }}>
                  {t('footer.references')}
                </a>
              </li>
              <li>
                <Link to="/articles/">{t('nav.articles')}</Link>
              </li>
              <li>
                <a
                  href={CAL_BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackEvent('Booking', 'Click', 'Footer nav');
                    trackGoal(1);
                  }}
                >
                  {t('nav.contact')}
                </a>
              </li>
            </ul>
          </div>

          <div className="footer-section">
            <p className="footer-subtitle">{t('footer.commitments')}</p>
            <ul className="footer-links">
              {locale === 'fr' ? (
                <>
                  <li><Link to="/a-propos/">{t('footer.about')}</Link></li>
                  <li><Link to="/securite-rgpd/">{t('footer.security')}</Link></li>
                  <li><Link to="/references/">{t('footer.references')}</Link></li>
                  <li><Link to="/hebergement-france/">{t('footer.hostingFrance')}</Link></li>
                  <li><Link to="/rgpd/">{t('footer.rgpd')}</Link></li>
                  <li><Link to="/webrtc/">WebRTC</Link></li>
                  <li><Link to="/open-source/">Open source</Link></li>
                </>
              ) : (
                <>
                  <li><Link to="/en/gdpr-video-compliance/">{t('footer.security')}</Link></li>
                  <li><Link to="/en/france-hosted-video-platform/">{t('footer.hostingFrance')}</Link></li>
                  <li><Link to="/en/webrtc-video-infrastructure/">WebRTC</Link></li>
                  <li><Link to="/en/video-api/">Video API</Link></li>
                  <li><Link to="/en/sfu-webrtc-infrastructure/">SFU WebRTC</Link></li>
                  <li><Link to="/en/custom-video-integration/">Custom integration</Link></li>
                  <li><Link to="/en/quote/">{t('navPages.quote')}</Link></li>
                </>
              )}
            </ul>
          </div>

          <div className="footer-section">
            <p className="footer-subtitle">{t('footer.infrastructure')}</p>
            <ul className="footer-links">
              {hubLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to}>{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="footer-section">
            <p className="footer-subtitle">{t('footer.followUs')}</p>
            <div className="social-links">
              <a href={LINKEDIN_COMPANY_URL} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn Leagora">
                <FiLinkedin aria-hidden />
              </a>
              <a href="https://twitter.com/leagora" target="_blank" rel="noopener noreferrer" aria-label="Twitter Leagora">
                <FiTwitter aria-hidden />
              </a>
              <a href="https://github.com/leagora" target="_blank" rel="noopener noreferrer" aria-label="GitHub Leagora">
                <FiGithub aria-hidden />
              </a>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; {currentYear} Leagora. {t('footer.rights')}.</p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
